import { z } from 'zod';
import { blockSchema, contentImageSuggestionSchema, mediaResultSchema } from './localAreaExpert.js';
import { socialMetadataSchema } from './localSocial.js';
import { safeSectionCtaUrl, sectionCtaSchema } from './localHomepage.js';

export const localArticleCategories = ['Community', 'Education', 'Lifestyle', 'Local Living', 'Other'] as const;
export const articleCtaTypes = ['assessment', 'contact', 'buy', 'sell', 'custom', 'info'] as const;
export const articleCtaSchema = z.object({
  enabled: z.boolean(),
  type: z.enum(articleCtaTypes),
  heading: z.string().trim().max(120),
  message: z.string().trim().max(400),
  buttonLabel: z.string().trim().max(80).optional(),
  customUrl: z.string().trim().max(2000).optional(),
}).strict().superRefine((value, context) => {
  if (value.enabled && value.type === 'custom' && !safeSectionCtaUrl(value.customUrl ?? '')) context.addIssue({ code: z.ZodIssueCode.custom, path: ['customUrl'], message: 'Use a public HTTPS destination without redirects or preview tokens' });
});
export type ArticleCta = z.infer<typeof articleCtaSchema>;
export const articleCtaOptions = {
  assessment: { label: 'Request a CMA', buttonLabel: 'Request a Property Assessment', path: 'valuation', heading: 'Thinking about selling your home?', message: "Before making a decision, understand what your property could realistically achieve in today's market." },
  contact: { label: 'Contact the Agent', buttonLabel: 'Contact the Agent', path: 'enquire', heading: 'Have a question about this story?', message: 'Get in touch for local insight and advice.' },
  buy: { label: 'Buy', buttonLabel: 'Explore properties', path: 'buy', heading: 'Ready to find a home?', message: 'Talk with your local property expert about your next move.' },
  sell: { label: 'Sell', buttonLabel: 'Speak to a property expert', path: 'sell', heading: 'Thinking about selling?', message: 'Talk with your local property expert about your property.' },
  custom: { label: 'Custom Link', buttonLabel: 'Learn more', path: null, heading: 'Discover more', message: 'Explore more with your local property expert.' },
  info: { label: 'Information only (no button)', buttonLabel: null, path: null, heading: 'Local insight from your agent', message: 'Questions about your area or property? Ask your agent.' },
} as const;
export function articleCtaDefaults(type: ArticleCta['type'] = 'assessment'): ArticleCta {
  return { enabled: false, type, heading: articleCtaOptions[type].heading, message: articleCtaOptions[type].message };
}
// Destinations are fixed in-site routes, never caller supplied, so no preview capability can leak into a CTA link.
export function articleCtaDestination(cta: ArticleCta | undefined, kwuid: string): { label: string; href: string; external: boolean } | null {
  if (!cta?.enabled || cta.type === 'info') return null;
  if (cta.type === 'custom') { const href = safeSectionCtaUrl(cta.customUrl ?? ''); return href ? { label: cta.buttonLabel || articleCtaOptions.custom.buttonLabel, href, external: true } : null; }
  if (!/^[0-9]{3,12}$/.test(kwuid)) return null;
  return { label: cta.buttonLabel || articleCtaOptions[cta.type].buttonLabel, href: `/vcard/${kwuid}/${articleCtaOptions[cta.type].path}`, external: false };
}
const ctaIntent = /(property assessment|\bcma\b|valuation|contact (me|us|the agent)|get in touch|request a|book a|free (valuation|assessment)|thinking (of|about) selling|\b(buy|sell)\b|property expert|explore properties)/i;
// Authored content is only ever inspected, never changed; a match just warns that two calls-to-action may appear.
export function articleCtaDuplicateWarning(data: { cta?: ArticleCta; blocks: Array<{ type: string; label?: string; href?: string; title?: string; text?: string }> }) {
  const cta = data.cta;
  if (!cta?.enabled) return null;
  const duplicate = data.blocks.some((block) => block.type === 'link' ? /\/vcard\/|\/(valuation|enquire)\b/i.test(block.href ?? '') || cta.type === 'custom' && Boolean(safeSectionCtaUrl(block.href ?? '')) && safeSectionCtaUrl(block.href ?? '') === safeSectionCtaUrl(cta.customUrl ?? '') || ctaIntent.test(block.label ?? '') : block.type === 'highlight' && ctaIntent.test(`${block.title ?? ''} ${block.text ?? ''}`));
  return duplicate ? 'This article already contains its own call-to-action. Both your written call-to-action and the automatic one may appear at the end of the article.' : null;
}

export const localContentStatuses = ['draft', 'scheduled', 'published', 'unpublished'] as const;
export type LocalContentStatus = typeof localContentStatuses[number];
export type LocalContentEffectiveStatus = LocalContentStatus | 'expired';
export const JOHANNESBURG_TIME_ZONE = 'Africa/Johannesburg';
type Lifecycle = { status: LocalContentStatus; publishAt: string | null; expiresAt: string | null };
// Evaluated on every read; there is no timer or scheduler. Start is inclusive and expiry is exclusive.
export function localContentEffectiveStatus(record: Lifecycle, now: Date): LocalContentEffectiveStatus {
  if (record.status === 'draft' || record.status === 'unpublished') return record.status;
  const time = now.getTime();
  if (record.expiresAt && time >= Date.parse(record.expiresAt)) return 'expired';
  if (record.status === 'scheduled') return record.publishAt && time >= Date.parse(record.publishAt) ? 'published' : 'scheduled';
  return 'published';
}
export const localContentIsPublic = (record: Lifecycle, now: Date) => localContentEffectiveStatus(record, now) === 'published';
export type LocalContentAction = 'edit' | 'view' | 'preview' | 'duplicate' | 'delete' | 'reschedule' | 'cancel_schedule' | 'unpublish' | 'republish' | 'publish_now';
export function localContentActions(status: LocalContentEffectiveStatus, capabilities: { republish: boolean }): LocalContentAction[] {
  if (status === 'draft') return ['edit', 'preview', 'duplicate', 'delete'];
  if (status === 'scheduled') return ['edit', 'preview', 'reschedule', 'cancel_schedule'];
  if (status === 'published') return ['view', 'edit', 'unpublish', 'duplicate'];
  return ['edit', 'preview', ...(capabilities.republish ? ['republish' as const] : []), 'delete'];
}
// South Africa observes no daylight saving, so the Africa/Johannesburg offset is a constant +02:00.
export function johannesburgLocalToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(`${value}:00+02:00`);
  // Round-trip rejects impossible dates such as 31 February.
  return Number.isNaN(date.getTime()) || isoToJohannesburgLocal(date.toISOString()) !== value ? null : date.toISOString();
}
export function isoToJohannesburgLocal(iso: string | null): string {
  if (!iso) return '';
  return new Date(Date.parse(iso) + 2 * 3_600_000).toISOString().slice(0, 16);
}
export function formatJohannesburg(iso: string | null) {
  return iso ? `${new Intl.DateTimeFormat('en-ZA', { timeZone: JOHANNESBURG_TIME_ZONE, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))} SAST` : '';
}
const instant = z.string().datetime({ offset: true });
export const localArticleLifecycleRequestSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('publish_now'), expectedVersion: z.number().int().positive(), expiresAt: instant.nullable().optional() }).strict(),
  z.object({ action: z.literal('schedule'), expectedVersion: z.number().int().positive(), publishAt: instant, expiresAt: instant.nullable().optional() }).strict(),
  z.object({ action: z.literal('reschedule'), expectedVersion: z.number().int().positive(), publishAt: instant, expiresAt: instant.nullable().optional() }).strict(),
  z.object({ action: z.literal('cancel_schedule'), expectedVersion: z.number().int().positive() }).strict(),
  z.object({ action: z.literal('unpublish'), expectedVersion: z.number().int().positive() }).strict(),
  z.object({ action: z.literal('republish'), expectedVersion: z.number().int().positive(), expiresAt: instant.nullable().optional() }).strict(),
]);
export type LocalArticleLifecycleRequest = z.infer<typeof localArticleLifecycleRequestSchema>;

export const localArticleDataSchema = z.object({
  title: z.string().trim().min(1, 'Article title is required').max(160),
  category: z.enum(localArticleCategories),
  excerpt: z.string().trim().max(500),
  coverMediaId: z.string().uuid().nullable(),
  blocks: z.array(blockSchema).min(1).max(100),
  socialCaption: z.string().trim().max(500),
  imageSuggestions: z.array(contentImageSuggestionSchema).max(8),
  cta: articleCtaSchema.optional(),
  social: socialMetadataSchema.optional(),
}).strict();
export type LocalArticleData = z.infer<typeof localArticleDataSchema>;

export const localArticleRecordSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  status: z.enum(localContentStatuses),
  featured: z.boolean(),
  publishAt: z.string().datetime({ offset: true }).nullable(),
  expiresAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  data: localArticleDataSchema,
}).strict();
export type LocalArticleRecord = z.infer<typeof localArticleRecordSchema>;

export const localArticlePreviewSchema = z.object({
  mode: z.literal('local_article_preview'),
  sectionCta: sectionCtaSchema.optional(),
  kwuid: z.string().regex(/^[0-9]{3,12}$/),
  items: z.array(localArticleRecordSchema).max(100),
  media: z.array(mediaResultSchema).max(100),
  featuredId: z.string().uuid().nullable(),
  featuredIds: z.array(z.string().uuid()).max(3),
  publicIds: z.array(z.string().uuid()).max(100),
  privatePreviewId: z.string().uuid().nullable(),
  serverTime: z.string().datetime(),
  expiresAt: z.string().datetime(),
}).strict();